import { Store } from '@nextforce/api'
import { Button, Divider, ListItem, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { useCallback, useState } from 'react'
import { View } from 'react-native'
import { Collapsible } from './list/collapsible'

interface Props {
  title: string
  value: string | undefined
  activeStore?: Store
  selectedStores?: Store[]
  stores: Store[]
  onChange(selectedStores: Store[]): void
  required?: boolean
}

export function StoreChooser({
  title,
  stores,
  selectedStores,
  activeStore,
  onChange,
  required,
  value,
}: Props) {
  const styles = useStyles()
  const [expanded, setExpanded] = useState<boolean>(true)

  const [currentSelectedStores, setCurrentSelectedStores] = useState<Store[]>(
    selectedStores && selectedStores.length > 0
      ? selectedStores
      : activeStore
      ? [activeStore]
      : []
  )

  const onToggleStores = useCallback(
    (selectedStore: Store) => {
      const newSelectedStores = currentSelectedStores.some(
        s => s.id === selectedStore.id
      )
        ? currentSelectedStores.filter(s => s.id !== selectedStore.id)
        : [...currentSelectedStores, selectedStore]

      setCurrentSelectedStores(newSelectedStores)
      onChange(newSelectedStores)
    },
    [currentSelectedStores, onChange]
  )

  const onSelectAllStores = useCallback(() => {
    setCurrentSelectedStores(stores)
    onChange(stores)
  }, [onChange, stores])

  const onPressClearAll = useCallback(() => {
    setCurrentSelectedStores([])
    onChange([])
  }, [onChange])

  return (
    <Collapsible
      title={title}
      value={value}
      expanded={expanded}
      onPress={() => setExpanded(!expanded)}
      required={required}
    >
      <View style={styles.container}>
        <Button
          buttonStyle={styles.buttonContainer}
          title={t('all')}
          type="outline"
          disabled={stores.length === currentSelectedStores.length}
          onPress={onSelectAllStores}
        />
        <Button
          buttonStyle={styles.buttonContainer}
          title={t('none')}
          type="outline"
          disabled={currentSelectedStores.length === 0}
          onPress={onPressClearAll}
        />
      </View>
      <Divider />
      {stores.map((item, index) => (
        <ListItem
          key={index}
          onPress={() => onToggleStores(item)}
          bottomDivider
        >
          <ListItem.Content>
            <ListItem.Title style={styles.option}>{item.name}</ListItem.Title>
          </ListItem.Content>
          <ListItem.CheckBox
            checked={currentSelectedStores.includes(item)}
            checkedColor={styles.checkboxChecked.color}
            uncheckedColor={styles.checkboxUnchecked.color}
            checkedIcon="check"
            uncheckedIcon=""
            onPress={() => {
              onToggleStores(item)
            }}
          />
        </ListItem>
      ))}
    </Collapsible>
  )
}

export const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    margin: 5,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  option: {
    marginHorizontal: 15,
    color: theme.colors?.grey2,
  },
  checkboxChecked: {
    color: theme.colors?.primary,
  },
  checkboxUnchecked: {
    color: theme.colors?.grey5,
  },
  buttonContainer: {
    borderRadius: 25,
    width: 70,
    height: 35,
    marginLeft: 5,
    marginBottom: 5,
    padding: 0,
    borderWidth: 1,
  },
}))
